import { PageFallback } from '@bookora/ui';
import { Permission } from '@bookora/shared';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/token-store';
import { useOnboarding } from '@/features/settings/hooks';
import { useCan } from '@/permissions/use-can';

import { setupHref, wizardKeyForServerStep } from './setup-steps';
import { isSetupPostponed, useAutoCompleteSetup } from './use-setup';

/**
 * Brings an owner whose clinic is not set up back to the wizard.
 *
 * Signup lands on `/setup` once. Every login after that lands on `/`, and a
 * half-finished owner used to find a dashboard of empty cards with a small
 * progress widget — the calendar offering no slot and nothing saying why.
 *
 * It redirects ONLY when all of these hold, so it can never trap anyone:
 *   · the path is exactly `/` — a deep link, a notification, `?next=` is
 *     always honoured;
 *   · the viewer may edit settings (a receptionist cannot finish setup);
 *   · nobody is impersonating (support must see the clinic as it is);
 *   · the checklist has loaded, is not complete and cannot yet be completed;
 *   · the owner has not chosen «بعداً» in this browser session.
 *
 * It also completes setup the moment every required step is done
 * (`useAutoCompleteSetup`), wherever the steps were done. On a failed read it
 * renders the panel: an outage must not hide the dashboard behind a spinner.
 */
export function SetupResumeGate() {
  const { pathname } = useLocation();
  const { activeClinicId, impersonatedBy } = useAuth();
  const canEdit = useCan(Permission.SETTINGS_EDIT);
  const onboarding = useOnboarding();
  useAutoCompleteSetup();

  const candidate = pathname === '/' && canEdit && !impersonatedBy;
  if (!candidate || isSetupPostponed(activeClinicId)) return <Outlet />;
  if (onboarding.isLoading) return <PageFallback />;

  const data = onboarding.data;
  if (!data || data.completedAt || data.canComplete) return <Outlet />;

  const next = data.steps.find((step) => step.required && !step.done);
  const screen = next ? wizardKeyForServerStep(next.key) : null;
  return <Navigate to={setupHref(screen ?? 'profile')} replace />;
}
